import {Injectable, signal, inject} from "@angular/core";
import {NavigationEnd, Router} from "@angular/router";
import {filter} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {UserPreferenceService} from "src/app/services/ui/user-preference.service";
import {CurrentTenantStore} from "../tenant/current-tenant.store";
import {AuthenticationService} from "../auth/authentication.service";

const OPENED: MenuState = {open: true, width: 275};
const CLOSED: MenuState = {open: false, width: 80};

@Injectable({
	providedIn: 'root'
})
export class MenuStateService {
	private router = inject(Router);
	private tenantStore = inject(CurrentTenantStore);
	private authenticationService = inject(AuthenticationService);
	private userPreferenceService = inject(UserPreferenceService);

	private readonly _state = signal<MenuState>(OPENED);
	/** The desktop menu, unfolded or folded, and the width it takes. */
	readonly state = this._state.asReadonly();

	/**
	 * Menu en overlay (< 960px, voir LayoutStore.isHandset) : état volatil,
	 * indépendant de la préférence déplié/replié persistée du desktop.
	 */
	readonly mobileOpen = signal(false);

	constructor() {
		// Referme l'overlay mobile après chaque navigation.
		this.router.events
			.pipe(
				filter(event => event instanceof NavigationEnd),
				takeUntilDestroyed()
			)
			.subscribe(() => this.closeMobileMenu());

		// The folded / unfolded preference of this user on this workspace.
		this.tenantStore.whenLoaded()
			.subscribe(tenant => {
				const userId = this.authenticationService.getUserInfo()?.id;
				const preference = userId ? this.userPreferenceService.getUserPreferences(tenant.configuration.id, userId)?.menuPreference : undefined;
				if (preference) {
					this._state.set(preference.isExpanded ? OPENED : CLOSED);
				}
			});
	}

	toggleMobileMenu(): void {
		this.mobileOpen.update(open => !open);
	}

	closeMobileMenu(): void {
		this.mobileOpen.set(false);
	}

	openMenu(): void {
		this.setAndSave(OPENED);
	}

	closeMenu(): void {
		this.setAndSave(CLOSED);
	}

	/** A fold / unfold is saved as this user's preference on this workspace. */
	private setAndSave(state: MenuState): void {
		this._state.set(state);
		const userId = this.authenticationService.getUserInfo()?.id;
		const tenantId = this.tenantStore.currentTenantId();
		if (userId && tenantId) {
			this.userPreferenceService.setUserPreferences(tenantId, userId, {menuPreference: {isExpanded: state.open}});
		}
	}
}

export interface MenuState {
	open: boolean;
	width: number;
}
